import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { adminService } from '../../services/api';
import EmploymentStatus, { employmentErrorLabel, employmentSyncLabel } from './EmploymentStatus';

const Fields = styled.div`display: grid; gap: 0.8rem; margin: 1rem 0; label { display: grid; gap: 0.3rem; } input, select, textarea { padding: 0.55rem; border: 1px solid var(--border-subtle); border-radius: 4px; width: 100%; box-sizing: border-box; }`;
const Comparison = styled.div`
  margin: 1rem 0; padding: 0.8rem; border: 1px solid var(--border-subtle); border-radius: 4px;
  h3 { font-size: 1rem; margin: 0 0 0.6rem; }
  p { margin: 0.4rem 0; }
  details { margin-top: 0.6rem; font-size: 0.8rem; overflow-wrap: anywhere; }
`;
const FactColumns = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 235px), 1fr)); gap: 1rem;
  section { min-width: 0; overflow-wrap: anywhere; }
  h4 { margin: 0 0 0.4rem; }
  dl { margin: 0; display: grid; grid-template-columns: auto 1fr; gap: 0.3rem 0.8rem; }
  dd { margin: 0; }
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end;`;

const crmMatch = (placement, remotePlacements = []) => remotePlacements.find((remote) =>
  remote.tempusPlacementId === placement?.id || (placement?.crmPlacementId && remote.crmPlacementId === placement.crmPlacementId));
const datesDiffer = (local, remote) => Boolean(local && remote
  && (local.startDate !== remote.startDate || (local.endDate || null) !== (remote.endDate || null)));
const decisionDiffers = (local, remote) => Boolean(local?.latestOverride?.id && remote?.latestOverrideId
  && local.latestOverride.id !== remote.latestOverrideId);
const comparisonNeedsReview = (local, crmState) => {
  const remote = crmMatch(local, crmState?.placements);
  const outcome = crmState?.overrideStates?.find((item) => item.overrideId === local.latestOverride?.id);
  return datesDiffer(local, remote) || decisionDiffers(local, remote) || ['cancelled', 'superseded'].includes(outcome?.status);
};
const outcomeLabel = (status) => ({ applied: 'Applied', scheduled: 'Scheduled', cancelled: 'Cancelled', superseded: 'Superseded' }[status] || 'Not confirmed');
const acceptedDecision = (decision) => Boolean(decision?.crmOutcome || ['synced', 'scheduled'].includes(decision?.status));
const FactDates = ({ placement }) => <dl>
  <dt>Start</dt><dd>{placement.startDate || 'Not provided'}</dd>
  <dt>End</dt><dd>{placement.endDate || 'Open ended'}</dd>
  {placement.status && <><dt>Status</dt><dd>{placement.status}</dd></>}
</dl>;

function EmploymentFacts({ facts }) {
  const remotePlacements = facts.crmState?.placements || [];
  const pairs = (facts.placements || []).map((local) => ({ local, remote: crmMatch(local, remotePlacements) }));
  remotePlacements.filter((remote) => !pairs.some((pair) => pair.remote === remote)).forEach((remote) => pairs.push({ local: null, remote }));
  return <div aria-label="Employment facts from Tempus and VikarCRM">
    <p>Compare the saved employment in both systems before making a new decision. Dates include the first and last employment day.</p>
    {facts.crmAvailable && facts.crmState?.schemaVersion === 1 && <p>CRM supplied legacy employment details. Later cancellations and replacements cannot be confirmed until CRM supports the updated integration.</p>}
    {pairs.map(({ local, remote }) => {
      const decision = local?.latestOverride;
      const liveOutcome = facts.crmState?.overrideStates?.find((item) => item.overrideId === decision?.id);
      const outcome = liveOutcome || decision?.crmOutcome;
      const decisionStatus = ['cancelled', 'superseded'].includes(liveOutcome?.status) ? liveOutcome.status
        : decision?.status === 'synced' && comparisonNeedsReview(local, facts.crmState) ? 'review_required' : decision?.status;
      const latestOutcome = facts.crmState?.overrideStates?.find((item) => item.overrideId === remote?.latestOverrideId);
      const name = local?.customerName || (local ? `Customer ${local.customerId}` : 'CRM placement');
      const remoteCustomer = remote && remote.crmCompanyId === local?.crmCompanyId ? name : remote?.crmCompanyId;
      return <Comparison key={local?.id || remote.crmPlacementId} aria-label={`${name} placement comparison`}>
        <h3>{name}</h3>
        <FactColumns>
          <section aria-label="Saved in Tempus"><h4>Tempus</h4>{local ? <FactDates placement={local} /> : <p>No matching Tempus placement.</p>}</section>
          <section aria-label="Current in VikarCRM"><h4>VikarCRM</h4>{remote ? <>
            <FactDates placement={remote} />
            <p>Customer: {remoteCustomer}</p>
          </> : <p>{facts.crmAvailable ? 'No matching placement in the CRM response.' : 'CRM facts are unavailable.'}</p>}</section>
        </FactColumns>
        {datesDiffer(local, remote) && <p style={{ color: '#9a6a10' }}>Employment dates differ. Saving a correction keeps the employment history and sends the dates entered below to CRM.</p>}
        {decisionDiffers(local, remote) && <p style={{ color: '#9a6a10' }}>CRM has a newer decision for this placement. Review it before replacing it.{latestOutcome ? ` Latest CRM decision outcome: ${outcomeLabel(latestOutcome.status)}.` : ''}</p>}
        {decision && <p>{employmentSyncLabel(decisionStatus) || 'Employment decision recorded'}{decision.lastError ? `. ${employmentErrorLabel(decision.lastError)}` : ''}</p>}
        {outcome && <p>{liveOutcome ? 'Current CRM decision outcome' : 'Last recorded CRM decision outcome'}: <strong>{outcomeLabel(outcome.status)}</strong>.{['cancelled', 'superseded'].includes(outcome.status) ? ' Tempus dates have been preserved for review.' : ''}</p>}
        <details><summary>Placement identities</summary>
          <p>Tempus placement: {local?.id || 'Not linked'}</p>
          <p>CRM placement: {remote?.crmPlacementId || local?.crmPlacementId || 'Not linked'}</p>
          <p>Linked CRM customer: {local?.crmCompanyId || 'Not linked'}</p>
          {remote && <p>CRM customer: {remote.crmCompanyId}</p>}
          {remote?.latestOverrideId && <p>Latest CRM decision: {remote.latestOverrideId}</p>}
          {remote?.effectiveOverrideId && <p>Currently effective CRM decision: {remote.effectiveOverrideId}</p>}
          {decision && <p>Tempus decision: {decision.id}</p>}
          {outcome?.supersededByOverrideId && <p>Replacement CRM decision: {outcome.supersededByOverrideId}</p>}
        </details>
      </Comparison>;
    })}
    {facts.crmState && <details style={{ overflowWrap: 'anywhere' }}><summary>CRM snapshot</summary>
      <p>Employee: {facts.crmState.crmEmployeeId}</p><p>Revision: {facts.crmState.employmentRevision}</p>
    </details>}
  </div>;
}

export default function EmploymentOverrideModal({ employee, customers, onClose, onSaved }) {
  const [facts, setFacts] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('correct_employment');
  const [placementId, setPlacementId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [reviewed, setReviewed] = useState(false);

  useEffect(() => {
    let active = true;
    adminService.getEmployment(employee.id).then((data) => {
      if (!active) return;
      setFacts(data);
      const latest = data.placements?.find((p) => p.id === (data.currentEmploymentPlacementId || data.employmentPlacementId)) || data.placements?.[0];
      if (latest) {
        setPlacementId(latest.id);
        setStartDate(latest.startDate || '');
        setEndDate(latest.endDate || '');
        if (latest.latestOverride?.canRecover && data.crmAvailable) setAction('recover_start');
      } else setAction('start_employment');
    }).catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [employee.id]);

  const choosePlacement = (id) => {
    setPlacementId(id);
    setReviewed(false);
    const placement = facts.placements.find((p) => p.id === id);
    setStartDate(placement?.startDate || '');
    setEndDate(placement?.endDate || '');
  };
  const chooseAction = (value) => {
    setAction(value);
    setReviewed(false);
    if (value === 'recover_start' && !facts.placements.find((p) => p.id === placementId)?.latestOverride?.canRecover) {
      const recoverable = facts.placements.find((p) => p.latestOverride?.canRecover);
      if (recoverable) choosePlacement(recoverable.id);
    }
  };
  const selectedPlacement = facts?.placements?.find((p) => p.id === placementId);
  const recovering = action === 'recover_start';
  const currentFactsDiffer = facts?.placements?.some((local) => comparisonNeedsReview(local, facts.crmState));
  const requiresReview = Boolean(facts?.reviewRequired || recovering || currentFactsDiffer);
  const displayedStatus = facts?.employmentSyncStatus === 'synced' && currentFactsDiffer
    ? { ...facts, employmentSyncStatus: 'review_required', employmentSyncError: null } : facts;
  const saveBlocked = Boolean(facts?.uncertainDelivery || (recovering && (!facts?.crmAvailable || !selectedPlacement?.latestOverride?.canRecover)));

  const refresh = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const data = await adminService.getEmployment(employee.id);
      setFacts(data);
      setReviewed(false);
      setNotice('Employment facts refreshed. Compare the current details before saving.');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const retry = async (decision) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await adminService.retryEmployment(employee.id, decision.id);
      setNotice(acceptedDecision(decision)
        ? 'A check of the accepted decision has been queued. Refresh employment facts after CRM responds.'
        : 'The original decision has been queued for retry with its original dates and revision. Refresh employment facts after CRM responds.');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saveBlocked) { setError('Resolve the existing CRM decision before saving another employment change.'); return; }
    if (requiresReview && !reviewed) { setError('Confirm that you have compared the Tempus and CRM employment details.'); return; }
    if (!reason.trim()) { setError('Enter a reason for this employment correction.'); return; }
    if (endDate && startDate && endDate < startDate) { setError('Employment end must be on or after employment start.'); return; }
    setBusy(true); setError('');
    try {
      const data = await adminService.overrideEmployment(employee.id, {
        action,
        ...(action === 'start_employment' ? { customerId: Number(customerId) } : { placementId }),
        startDate, endDate: endDate || null, reason: reason.trim(),
        expectedRevision: facts.expectedRevision,
        expectedCrmRevision: facts.expectedCrmRevision,
      });
      onSaved(data);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return <Modal size="lg" title={`Employment · ${employee.firstName} ${employee.lastName}`} onClose={busy ? () => {} : onClose}>
    <p>Employment follows customer placements. A dated correction preserves earlier employment history and is sent to VikarCRM.</p>
    {error && <p role="alert" style={{ color: 'var(--act-danger)' }}>{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!facts ? <p>Loading employment dates…</p> : <form onSubmit={submit}>
      <EmploymentStatus employee={displayedStatus} />
      {!facts.crmAvailable && <p style={{ color: '#9a6a10' }}>CRM is currently unavailable. A saved correction will show its synchronization status until confirmed. {employmentErrorLabel(facts.crmError)}</p>}
      <EmploymentFacts facts={facts} />
      {facts.uncertainDelivery && <p role="alert">CRM may already have accepted an earlier decision. Retry that original decision to recover its confirmation before changing employment again.</p>}
      {facts.placements?.filter((p) => p.latestOverride?.canRetry).map((p) => <Comparison key={p.id}>
        <p>{p.customerName || `Customer ${p.customerId}`}: {acceptedDecision(p.latestOverride) ? 'check the current CRM outcome of the accepted decision.' : 'retrying keeps the original decision, dates and revision.'}</p>
        <Button type="button" variant="secondary" disabled={busy} onClick={() => retry(p.latestOverride)}>{acceptedDecision(p.latestOverride) ? 'Check CRM outcome' : 'Retry original decision'}</Button>
      </Comparison>)}
      <Button type="button" variant="secondary" onClick={refresh} disabled={busy}>Refresh employment facts</Button>
      <Fields>
        <label>Employment change<select value={action} disabled={busy} onChange={(e) => chooseAction(e.target.value)}>
          <option value="correct_employment" disabled={!facts.placements?.length}>Correct placement dates</option>
          <option value="end_employment" disabled={!facts.placements?.length}>End employment</option>
          <option value="start_employment">Start new employment</option>
          <option value="recover_start" disabled={!facts.crmAvailable || !facts.placements?.some((p) => p.latestOverride?.canRecover)}>Recover existing employment start</option>
        </select></label>
        {recovering && <p>Send a reviewed start for this existing placement. Its customer, placement identity and work history are retained.</p>}
        {action === 'start_employment' ? <label>Customer<select required disabled={busy} value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">Select customer</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.companyName}</option>)}
        </select></label> : <label>Placement<select required disabled={busy} value={placementId} onChange={(e) => choosePlacement(e.target.value)}>
          <option value="">Select placement</option>{facts.placements.map((p) => <option key={p.id} value={p.id} disabled={recovering && !p.latestOverride?.canRecover}>{p.customerName || `Customer ${p.customerId}`} · {p.startDate} – {p.endDate || 'Open ended'}</option>)}
        </select></label>}
        <label>Employment Start<input required disabled={busy || action === 'end_employment'} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
        <label>Employment End<input disabled={busy} required={action === 'end_employment'} type="date" min={startDate || undefined} value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
        <label>Reason<textarea required disabled={busy} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      </Fields>
      {requiresReview && <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: '1rem' }}>
        <input type="checkbox" style={{ marginTop: '0.35rem', flexShrink: 0 }} checked={reviewed} disabled={busy} onChange={(e) => setReviewed(e.target.checked)} />
        I have compared the Tempus and CRM employment details.
      </label>}
      <Actions>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button type="submit" disabled={busy || saveBlocked || (requiresReview && !reviewed)}>{recovering ? 'Recover employment start' : 'Save employment correction'}</Button>
      </Actions>
    </form>}
  </Modal>;
}
